import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { goBack } from "../../../utils/otherHelopers";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Container } from "react-bootstrap";
import { Star } from "lucide-react";
import {
  fetchCompanyDetail,
  updateCompanyDetails,
  removeCompanyPhoto,
  patchCompanyPhoto,
  deleteCompany,
  manualVerifyCompany,
  changeCompanyStarStatus,
  revokeCompanyVerification,
} from "../../../redux/slice/companySlice";
import {
  ArrowLeftIcon,
  Delete02Icon,
  EditIcon,
  AddressIcon,
  UserIcon2,
  SmartPhoneIcon,
  MessageAddIcon,
  VerifiedIcon,
} from "../../../assets/icons";
import { routes } from "../../../routes/pageRoutes";
import EditCompanyProfileModal from "../../../components/modals/company/EditCompanyProfileModal";
import DeletePopup from "../../../components/delete-popup";
import ConfirmModal from "../../../components/withModal/ConfirmModal";
import { useLoader } from "../../../context/LoaderContext";
import CommonButton from "../../../components/commonButton";
import PreviewModal from "../../../components/modals/previewModal";
import StatusChip from "../../../components/statusChip/StatusChip";
import { formatMomentDate } from "../../../utils/dateHelper";
import CustomTabs from "../../../components/custom-tabs/CustomTabs";
import { COMPANY_DETAIL_TABS } from "./constants";
import { usePermission } from "../../../hooks/usePermission";

import Overview from "./tabs/Overview";
import Profile from "./tabs/Profile";
import Posts from "./tabs/Posts";
import Verifications from "./tabs/Verifications";
import "./index.css";
import { useToast } from "../../../context/TostContext";

const MODULE = "company_management";

const renderTabContent = (activeTab) => {
  switch (activeTab) {
    case "overview":
      return <Overview />;
    case "profile":
      return <Profile />;
    case "posts":
      return <Posts />;
    case "verifications":
      return <Verifications />;
    default:
      return <Overview />;
  }
};

const renderHeaderContent = (onBack, onEditClick, hasUpdatePermission) => (
  <div className="d-flex align-items-center justify-content-between w-100">
    <div className="d-flex align-items-center gap-2">
      <button
        onClick={onBack}
        type="button"
        className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center"
      >
        <ArrowLeftIcon width={22} height={22} color="#7C2439" strokeWidth="2.2" />
      </button>
      <h4 className="fw-semibold mb-0" style={{ color: '#444050', fontSize: '18px' }}>
        Company Detail
      </h4>
    </div>
    {hasUpdatePermission && (
      <div className="action-buttons-container d-flex align-items-center gap-3">
        <CommonButton
          icon={<EditIcon strokeWidth="2.0" />}
          variant="secondary"
          title="Edit Details"
          type="button"
          onClick={onEditClick}
        />
      </div>
    )}
  </div>
);

const CompanyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { setLoading } = useLoader();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = usePermission();
  const toast = useToast();
  const activeTab = searchParams.get("tab") || COMPANY_DETAIL_TABS[0]?.key;

  const headerPortalTarget = document.getElementById("header-portal-target");

  const { companyDetail, updateCompanyLoading } = useSelector((state) => state.company);

  const coverInputRef = useRef(null);
  const logoInputRef = useRef(null);

  const [coverHovered, setCoverHovered] = useState(false);
  const [logoHovered, setLogoHovered] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [deletePopupConfig, setDeletePopupConfig] = useState({
    show: false,
    title: "",
    content: "",
    saveTitle: "Delete",
    onConfirm: () => { },
  });
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => { },
    confirmText: "Yes",
    cancelText: "No",
  });


  const [previewMedia, setPreviewMedia] = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    dispatch(fetchCompanyDetail(id))
      .then(() => {
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching company details:", err);
        setLoading(false);
      });
  }, [id, dispatch, setLoading]);

  const handlePhotoUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size cannot exceed 5MB.");
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    const formData = new FormData();
    formData.append(type, file);

    dispatch(updateCompanyDetails({ id: id, data: formData }))
      .unwrap()
      .then(() => {
        dispatch(patchCompanyPhoto({ type, photoData: { url: previewUrl, key: null } }));
        toast.success(`${type === "cover_photo" ? "Cover" : "Logo"} photo updated successfully`);
      })
      .catch((error) => {
        URL.revokeObjectURL(previewUrl);
        toast.error(error?.message || `Failed to update ${type === "cover_photo" ? "cover" : "logo"} photo`);
      });

    e.target.value = null;
  };

  const confirmRemovePhoto = (type) => {
    dispatch(removeCompanyPhoto({ id: id, type }))
      .unwrap()
      .then(() => {
        dispatch(patchCompanyPhoto({ type, photoData: null }));
        toast.success(`${type === "cover_photo" ? "Cover" : "Logo"} photo removed successfully`);
        setDeletePopupConfig((prev) => ({ ...prev, show: false }));
      })
      .catch((error) => {
        toast.error(error?.message || "Failed to remove photo");
        setDeletePopupConfig((prev) => ({ ...prev, show: false }));
      });
  };

  const handleRemovePhoto = (type) => {
    setDeletePopupConfig({
      show: true,
      title: `Remove ${type === "cover_photo" ? "Cover" : "Logo"} Photo`,
      content: `Are you sure you want to remove the ${type === "cover_photo" ? "cover" : "logo"} photo?`,
      saveTitle: "Remove",
      onConfirm: () => confirmRemovePhoto(type),
    });
  };

  const handleDeleteCompany = () => {
    dispatch(deleteCompany(id))
      .unwrap()
      .then(() => {
        toast.success("Company deleted successfully");
        setDeletePopupConfig((prev) => ({ ...prev, show: false }));
        navigate("/company");
      })
      .catch((error) => {
        toast.error(error?.message || "Failed to delete company");
        setDeletePopupConfig((prev) => ({ ...prev, show: false }));
      });
  };

  const handleStatusChange = () => {
    if (!companyDetail) return;
    const newStatus = companyDetail.status === "active" ? "inactive" : "active";
    const formData = new FormData();
    formData.append("status", newStatus);

    dispatch(updateCompanyDetails({ id: id, data: formData }))
      .unwrap()
      .then(() => {
        toast.success(`Status updated to ${newStatus}`);
        setDeletePopupConfig((prev) => ({ ...prev, show: false }));
      })
      .catch((error) => {
        toast.error(error?.message || "Failed to update status");
        setDeletePopupConfig((prev) => ({ ...prev, show: false }));
      });
  };

  const handleManualVerify = () => {
    dispatch(manualVerifyCompany(id))
      .unwrap()
      .then(() => {
        toast.success("Company manually verified successfully");
        dispatch(fetchCompanyDetail(id));
      })
      .catch((error) => {
        toast.error(error?.message || "Failed to verify company");
      });
  };

  const handleRevokeVerify = () => {
    dispatch(revokeCompanyVerification(id))
      .unwrap()
      .then(() => {
        toast.success("Company verification revoked successfully");
        dispatch(fetchCompanyDetail(id));
      })
      .catch((error) => {
        toast.error(error?.message || "Failed to revoke company verification");
      });
  };

  const handleStarStatusChange = () => {
    const newStatus = !companyDetail?.is_star;
    dispatch(changeCompanyStarStatus({ id: id, is_star: newStatus }))
      .unwrap()
      .then((res) => {
        toast.success(res.message || "Star status updated successfully");
      })
      .catch((error) => {
        toast.error(error?.message || "Failed to update star status");
      });
  };

  const handleUpsertCompanyProfile = (data) => {
    dispatch(updateCompanyDetails({ id: id, data }))
      .unwrap()
      .then(() => {
        toast.success("Company profile updated successfully");
        setShowEditModal(false);
      })
      .catch((error) => {
        toast.error(error?.message || "Failed to update profile");
      });
  };


  const handleBack = () => {
    goBack(navigate, location, routes.allCompanies);
  };

  if (!companyDetail) {
    return null;
  }

  return (
    <>
      {headerPortalTarget && createPortal(
        renderHeaderContent(handleBack, () => setShowEditModal(true), hasPermission(MODULE, "update")),
        headerPortalTarget
      )}
      <Container fluid className="p-0">
        <div className="user-details-page">
          <div className="grid overflow-hidden mt-4">
            {/* Left Side Info Pane */}
            <div className="g-col-9 overflow-auto scroll-0">
              {/* Header / Profile Info Card */}
              <div className="overflow-hidden position-relative mb-30 bg-white rounded-3">
                {/* Cover Photo */}
                <div
                  className="position-relative profile-cover g-col-2"
                  style={{ height: "180px", cursor: "pointer", backgroundColor: "#f8f9fa" }}
                  onMouseEnter={() => setCoverHovered(true)}
                  onMouseLeave={() => setCoverHovered(false)}
                >
                  <img
                    src={companyDetail?.profile?.cover_photo?.url || "/images/AllUser/UserHeader.png"}
                    alt="cover"
                    className="w-100 h-100 object-fit-cover"
                  />
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center cover-photo-overlay"
                    style={{
                      background: "rgba(0,0,0,0.45)",
                      gap: "15px",
                      opacity: coverHovered ? 1 : 0,
                      transition: "opacity 0.2s",
                      pointerEvents: coverHovered ? "auto" : "none",
                    }}
                    onClick={(e) => {
                      if (e.target === e.currentTarget) {
                        setPreviewMedia([
                          {
                            url: companyDetail?.profile?.cover_photo?.url || "/images/AllUser/UserHeader.png",
                            title: "Cover Photo",
                            type: "image",
                          },
                        ]);
                      }
                    }}
                  >
                    {hasPermission(MODULE, "update") && (
                      <div
                        className="bg-white rounded-circle shadow-sm d-flex align-items-center justify-content-center avatar-action-btn"
                        onClick={() => coverInputRef.current?.click()}
                        title="Change Cover Photo"
                      >
                        <EditIcon width={14} height={14} color="#333" />
                      </div>
                    )}
                    {hasPermission(MODULE, "update") && companyDetail?.profile?.cover_photo?.url && (
                      <button
                        className="bg-white text-danger rounded-circle shadow-sm d-flex align-items-center justify-content-center avatar-action-btn"
                        onClick={() => handleRemovePhoto("cover_photo")}
                        title="Remove Cover Photo"
                      >
                        <Delete02Icon size={14} />
                      </button>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={coverInputRef}
                    className="d-none"
                    accept="image/*"
                    onChange={(e) => handlePhotoUpload(e, "cover_photo")}
                  />
                </div>

                {/* Company Logo + Name Card */}
                <div className="p-4 grid gap-3 shadow-none align-items-start">
                  <div className="g-col-2 text-center" style={{ gridRow: "span 2" }}>
                    <div
                      className="position-relative rounded-circle overflow-hidden profile-avatar-wrapper shadow-sm"
                      style={{
                        width: "110px",
                        height: "110px",
                        marginTop: "-60px",
                        border: "4px solid white",
                        zIndex: 10,
                        backgroundColor: "white",
                        cursor: "pointer",
                      }}
                      onMouseEnter={() => setLogoHovered(true)}
                      onMouseLeave={() => setLogoHovered(false)}
                    >
                      <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-light">
                        {companyDetail?.profile?.logo?.url ? (
                          <img
                            src={companyDetail.profile.logo.url}
                            alt="logo"
                            className="w-100 h-100 object-fit-cover"
                          />
                        ) : (
                          <UserIcon2 width={48} height={48} color="#adb5bd" strokeWidth="1.5" />
                        )}
                      </div>
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center profile-avatar-overlay"
                        style={{
                          background: "rgba(0,0,0,0.45)",
                          gap: "8px",
                          transition: "opacity 0.2s",
                          opacity: logoHovered ? 1 : 0,
                          pointerEvents: logoHovered ? "auto" : "none",
                        }}
                        onClick={(e) => {
                          if (e.target === e.currentTarget && companyDetail?.profile?.logo?.url) {
                            setPreviewMedia([
                              {
                                url: companyDetail.profile.logo.url,
                                title: "Company Logo",
                                type: "image",
                              },
                            ]);
                          }
                        }}
                      >
                        {hasPermission(MODULE, "update") && (
                          <div
                            className="bg-white rounded-circle shadow-sm d-flex align-items-center justify-content-center avatar-action-btn"
                            onClick={() => logoInputRef.current?.click()}
                            title="Change Logo"
                          >
                            <EditIcon width={14} height={14} color="#333" />
                          </div>
                        )}
                        {hasPermission(MODULE, "update") && companyDetail?.profile?.logo?.url && (
                          <button
                            className="bg-white text-denger rounded-circle shadow-sm d-flex align-items-center justify-content-center avatar-action-btn"
                            onClick={() => handleRemovePhoto("logo")}
                            title="Remove Logo"
                          >
                            <Delete02Icon size={14} />
                          </button>
                        )}
                      </div>
                      <input
                        type="file"
                        ref={logoInputRef}
                        className="d-none"
                        accept="image/*"
                        onChange={(e) => handlePhotoUpload(e, "logo")}
                      />
                    </div>
                  </div>

                  <div className="g-col-10 d-flex flex-column justify-content-center">
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <div className="d-flex align-items-center gap-2">
                        <h5 className="mb-0 profile-user-name">
                          {companyDetail?.name || "-"}
                          {companyDetail?.tagline && (
                            <span className="small text-muted fw-normal ms-2">- {companyDetail.tagline}</span>
                          )}
                        </h5>
                        {companyDetail?.is_verified && (
                          <VerifiedIcon size={18} className="text-success" />
                        )}
                        {companyDetail?.is_star && (
                          <Star size={18} fill="#ffc107" color="#ffc107" />
                        )}
                      </div>
                      {companyDetail?.status && (
                        <StatusChip
                          pill
                          bg={companyDetail.status === "active" ? "success" : "danger"}
                          title={companyDetail.status.toUpperCase()}
                        />
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-3 mt-1">
                      <p className="mb-0 profile-role text-muted small">
                        {companyDetail?.category?.name || "-"}
                      </p>
                      {companyDetail?.profession?.name && (
                        <>
                          <span className="text-muted">•</span>
                          <p className="mb-0 profile-role text-muted small">
                            {companyDetail.profession.name}
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Left sidebar info columns */}
                  <div className="profile-info-left g-col-4 border-end pe-3">
                    <div className="d-flex flex-column contact-list gap-2">
                      <div className="d-flex align-items-center gap-2" title={companyDetail?.profile?.email || ""}>
                        <i className="primary">
                          <MessageAddIcon size={34} className="flex-shrink-0" />
                        </i>
                        <span className="contact-text text-truncate small">{companyDetail?.profile?.email || "-"}</span>
                      </div>
                      <div className="d-flex align-items-center gap-2" title={companyDetail?.profile?.mobile_number || ""}>
                        <i className="primary">
                          <SmartPhoneIcon size={34} className="flex-shrink-0" />
                        </i>
                        <span className="contact-text text-truncate small">{companyDetail?.profile?.mobile_number || "-"}</span>
                      </div>
                      <div
                        className="d-flex align-items-center gap-2"
                        title={[
                          companyDetail?.profile?.city?.name,
                          companyDetail?.profile?.city?.state?.name,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      > <i className="primary">
                          <AddressIcon size={34} className="flex-shrink-0" />
                        </i>
                        <span className="contact-text text-truncate small">
                          {[
                            companyDetail?.profile?.city?.name,
                            companyDetail?.profile?.city?.state?.name,
                          ]
                            .filter(Boolean)
                            .join(", ") || "-"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right Info Column */}
                  <div className="grid flex-fill detail-column g-col-6">

                    <span className="detail-label small text-muted g-col-3">Company ID</span>
                    <span className="detail-value small text-nowrap g-col-9">{companyDetail?.id || "-"}</span>


                    <span className="detail-label small text-muted g-col-3">Created At</span>
                    <span className="detail-value small g-col-9">
                      {formatMomentDate(companyDetail?.created_at, "DD MMM YYYY, hh:mm A")}
                    </span>


                    <span className="detail-label small text-muted g-col-3">Updated At</span>
                    <span className="detail-value small g-col-9">
                      {formatMomentDate(companyDetail?.updated_at, "DD MMM YYYY, hh:mm A")}
                    </span>

                  </div>
                </div>
              </div>
            </div>

            {/* Right Column Quick Actions Pane */}
            <div className="g-col-3 overflow-auto scroll-0">
              <div className="border bg-white p-3 shadow-md rounded-3">
                <h6 className="fw-semibold mb-3">Quick Actions</h6>
                <div className="grid gap-2">
                  {hasPermission(MODULE, "update") && (
                    <>
                      <CommonButton
                        title="Edit Details"
                        icon={<EditIcon width={16} height={16} />}
                        className="g-col-12"
                        variant="primary"
                        onClick={() => setShowEditModal(true)}
                      />
                      <CommonButton
                        title={companyDetail?.status === "active" ? "Deactivate Company" : "Activate Company"}
                        icon={<UserIcon2 size={16} />}
                        className="g-col-12"
                        variant={companyDetail?.status === "active" ? "outline-danger" : "outline-success"}
                        onClick={() => {
                          const isActive = companyDetail?.status === "active";
                          setDeletePopupConfig({
                            show: true,
                            title: isActive ? "Deactivate Company" : "Activate Company",
                            content: "Are you sure you want to change the status of this company?",
                            saveTitle: isActive ? "Deactivate" : "Activate",
                            onConfirm: handleStatusChange,
                          });
                        }}
                      />
                      <CommonButton
                        title={companyDetail?.is_star ? "Remove Star Status" : "Mark as Star Company"}
                        icon={<Star size={16} fill={companyDetail?.is_star ? "currentColor" : "none"} />}
                        className="g-col-12"
                        variant={companyDetail?.is_star ? "warning" : "outline-warning"}
                        onClick={() => {
                          const newStatus = !companyDetail?.is_star_company;
                          setConfirmModalConfig({
                            isOpen: true,
                            title: newStatus ? "Remove Star Status" : "Mark as Star Company",
                            message: `Are you sure you want to ${newStatus ? "remove star status from this company" : "mark this company as a star company"}?`,
                            confirmText: newStatus ? "Remove Star" : "Mark Star",
                            cancelText: "Cancel",
                            onConfirm: handleStarStatusChange,
                          });
                        }}
                      />
                    </>
                  )}
                  {hasPermission("company_verification", "verify") && !companyDetail?.is_verified && (
                    <CommonButton
                      title="Verify Company"
                      icon={<UserIcon2 size={16} />}
                      className="g-col-12"
                      variant="success"
                      onClick={() => {
                        setConfirmModalConfig({
                          isOpen: true,
                          title: "Verify Company Manually",
                          message: "Are you sure you want to manually verify this company? This will set the company's status as verified and log a complimentary payment record.",
                          confirmText: "Verify",
                          cancelText: "Cancel",
                          onConfirm: handleManualVerify,
                        });
                      }}
                    />
                  )}
                  {hasPermission("company_verification", "verify") && companyDetail?.is_verified && (
                    <CommonButton
                      title="Revoke Verification"
                      icon={<UserIcon2 size={16} />}
                      className="g-col-12"
                      variant="danger"
                      onClick={() => {
                        setConfirmModalConfig({
                          isOpen: true,
                          title: "Revoke Company Verification",
                          message: "Are you sure you want to revoke this company's verification? This will set the company's status as unverified.",
                          confirmText: "Revoke",
                          cancelText: "Cancel",
                          onConfirm: handleRevokeVerify,
                        });
                      }}
                    />
                  )}
                  {hasPermission(MODULE, "delete") && (
                    <CommonButton
                      title="Delete Company"
                      icon={<Delete02Icon width={16} height={16} />}
                      className="g-col-12"
                      variant="danger"
                      onClick={() => {
                        setDeletePopupConfig({
                          show: true,
                          title: "Delete Company",
                          content: "Are you sure you want to delete this company? This action cannot be undone.",
                          saveTitle: "Delete",
                          onConfirm: handleDeleteCompany,
                        });
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs Row */}
          <div className="mt-3">
            <CustomTabs
              variant="underline"
              items={COMPANY_DETAIL_TABS}
              accessor="tab"
              onChange={(key) => {
                const updated = new URLSearchParams(searchParams);
                updated.set("tab", key);
                if (key !== "profile") {
                  updated.delete("profile-tabs");
                }
                setSearchParams(updated);
              }}
            />
          </div>

          {/* Tab Content Rendering */}
          <div className="mt-4">{renderTabContent(activeTab)}</div>
        </div>
      </Container>



      <EditCompanyProfileModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleUpsertCompanyProfile}
        company={companyDetail}
        size="lg"
        isLoading={updateCompanyLoading}
      />
      {/* Popups & Modals */}
      <DeletePopup
        show={deletePopupConfig.show}
        onHide={() => setDeletePopupConfig((prev) => ({ ...prev, show: false }))}
        title={deletePopupConfig.title}
        content={deletePopupConfig.content}
        saveTitle={deletePopupConfig.saveTitle}
        onConfirm={deletePopupConfig.onConfirm}
      />

      <ConfirmModal
        isOpen={confirmModalConfig.isOpen}
        onClose={() => setConfirmModalConfig((prev) => ({ ...prev, isOpen: false }))}
        title={confirmModalConfig.title}
        message={confirmModalConfig.message}
        onConfirm={confirmModalConfig.onConfirm}
        confirmText={confirmModalConfig.confirmText}
        cancelText={confirmModalConfig.cancelText}
        className="confirm-modal"
      />
      <PreviewModal
        show={!!previewMedia}
        onHide={() => setPreviewMedia(null)}
        previewMedia={previewMedia}
      />
    </>
  );
};

export default CompanyDetails;
